/**
 * 认证状态管理模块
 * 
 * 使用 Zustand 进行状态管理
 * 支持 localStorage 持久化，自动同步认证令牌和用户信息
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import {
  setAuthToken,
  setUserInfo,
  clearAuthData,
} from '@/lib/api/client'

/* ============================================
 * 类型定义
 * ============================================ */

/** 用户信息类型（与后端 AdminInfoDTO 对应） */
export interface UserInfo {
  /** 用户 ID */
  id: string
  /** 用户名 */
  username: string
  /** 真实姓名 */
  realName?: string
  /** 邮箱 */
  email?: string
  /** 手机号 */
  mobile?: string
  /** 状态 */
  status?: string
  /** 角色名称 */
  roleName?: string
  /** 角色编码 */
  roleCode?: string
  /** 权限列表 */
  permissions?: string[]
  /** 可切换的业务线列表（后端按角色菜单计算，不含平台级 platform） */
  allowedBizLines?: string[]
  /** 最后登录时间 */
  lastLoginTime?: string
  /** 最后登录IP */
  lastLoginIp?: string
}

/** 认证状态 */
export interface AuthState {
  /** 是否已登录 */
  isAuthenticated: boolean
  /** 认证令牌 */
  token: string | null
  /** 用户信息 */
  user: UserInfo | null
  /** 登录加载状态 */
  isLoading: boolean
}

/** 认证操作方法 */
export interface AuthActions {
  /** 设置认证信息（登录成功后调用） */
  login: (token: string, user: UserInfo) => void
  /** 更新用户信息 */
  updateUser: (user: Partial<UserInfo>) => void
  /** 更新令牌 */
  updateToken: (token: string) => void
  /** 登出（清除认证信息） */
  logout: () => void
  /** 设置加载状态 */
  setLoading: (loading: boolean) => void
  /** 检查是否已认证 */
  checkAuth: () => boolean
}

/** 完整的认证 Store 类型 */
export type AuthStore = AuthState & AuthActions

/* ============================================
 * 初始状态
 * ============================================ */

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  user: null,
  isLoading: false,
}

/* ============================================
 * Store 创建
 * ============================================ */

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // 初始状态
      ...initialState,

      // 登录：设置令牌和用户信息
      login: (token: string, user: UserInfo) => {
        // 同步到 localStorage（与 API 客户端共享）
        setAuthToken(token)
        setUserInfo(user)

        set({
          isAuthenticated: true,
          token,
          user,
          isLoading: false,
        })
      },

      // 更新用户信息
      updateUser: (userData: Partial<UserInfo>) => {
        const currentUser = get().user
        if (!currentUser) return

        const updatedUser = { ...currentUser, ...userData }
        setUserInfo(updatedUser)

        set({ user: updatedUser })
      },

      // 更新令牌
      updateToken: (token: string) => {
        setAuthToken(token)
        set({ token })
      },

      // 登出：清除所有认证信息
      logout: () => {
        clearAuthData()
        set(initialState)
      },

      // 设置加载状态
      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      // 检查是否已认证
      checkAuth: () => {
        const { token, user } = get()
        return !!(token && user)
      },
    }),
    {
      name: 'admin_auth_store',
      storage: createJSONStorage(() => localStorage),
      // 选择性持久化字段
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // 从存储恢复状态时，同步到 API 客户端使用的 localStorage 键
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          setAuthToken(state.token)
        }
        if (state?.user) {
          setUserInfo(state.user)
        }
      },
    }
  )
)

/* ============================================
 * 选择器 Hooks
 * ============================================ */

/** 获取当前用户信息 */
export const useUser = () => useAuthStore((state) => state.user)

/** 获取认证状态 */
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)

/** 获取加载状态 */
export const useAuthLoading = () => useAuthStore((state) => state.isLoading)

/** 获取认证令牌 */
export const useToken = () => useAuthStore((state) => state.token)

/* ============================================
 * 权限检查工具
 * ============================================ */

/**
 * 检查用户是否拥有指定角色
 * @param role - 角色标识
 * @returns 是否拥有该角色
 */
export const hasRole = (role: string): boolean => {
  const user = useAuthStore.getState().user
  return user?.roleCode === role
}

export const hasPermission = (permission: string): boolean => {
  const user = useAuthStore.getState().user
  return user?.permissions?.includes(permission) ?? false
}

export const hasAnyRole = (roles: string[]): boolean => {
  const user = useAuthStore.getState().user
  if (!user?.roleCode) return false
  return roles.includes(user.roleCode)
}

export const hasAllRoles = (roles: string[]): boolean => {
  const user = useAuthStore.getState().user
  if (!user?.roleCode) return false
  return roles.includes(user.roleCode)
}

export const hasAnyPermission = (permissions: string[]): boolean => {
  const user = useAuthStore.getState().user
  if (!user?.permissions) return false
  return permissions.some((permission) => user.permissions?.includes(permission))
}

export const hasAllPermissions = (permissions: string[]): boolean => {
  const user = useAuthStore.getState().user
  if (!user?.permissions) return false
  return permissions.every((permission) => user.permissions?.includes(permission))
}

export default useAuthStore